/*
 * CkEditor 5 Classic Editor, bundled for eSearcher 3
 */
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor'

import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials'
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph'

import SourceEditing from '@ckeditor/ckeditor5-source-editing/src/sourceediting'

import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold'
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic'
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline'

import List from '@ckeditor/ckeditor5-list/src/list'
import ListProperties from '@ckeditor/ckeditor5-list/src/listproperties'

import Indent from '@ckeditor/ckeditor5-indent/src/indent'
import IndentBlock from '@ckeditor/ckeditor5-indent/src/indentblock'

import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment'

import PageBreak from '@ckeditor/ckeditor5-page-break/src/pagebreak'

import Link from '@ckeditor/ckeditor5-link/src/link'
import AutoLink from '@ckeditor/ckeditor5-link/src/autolink'
import LinkImage from '@ckeditor/ckeditor5-link/src/linkimage'

import SelectAll from '@ckeditor/ckeditor5-select-all/src/selectall'
import RemoveFormat from '@ckeditor/ckeditor5-remove-format/src/removeformat'
import ShowBlocks from '@ckeditor/ckeditor5-show-blocks/src/showblocks'

import {Font} from "@ckeditor/ckeditor5-font";

import Image from '@ckeditor/ckeditor5-image/src/image'
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar'
import ImageUtils from '@ckeditor/ckeditor5-image/src/imageutils'
import ImageInline from '@ckeditor/ckeditor5-image/src/imageinline'
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle'
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize'
import AutoImage from '@ckeditor/ckeditor5-image/src/autoimage'
import ImageTextAlternative from '@ckeditor/ckeditor5-image/src/imagetextalternative'

import Table from '@ckeditor/ckeditor5-table/src/table'
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar'
import TableProperties from '@ckeditor/ckeditor5-table/src/tableproperties'
import TableCellProperties from '@ckeditor/ckeditor5-table/src/tablecellproperties'
import TableCaption from '@ckeditor/ckeditor5-table/src/tablecaption'
import TableColumnResize from '@ckeditor/ckeditor5-table/src/tablecolumnresize'

import FindAndReplace from '@ckeditor/ckeditor5-find-and-replace/src/findandreplace'
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough'
import Subscript from '@ckeditor/ckeditor5-basic-styles/src/subscript'
import Superscript from '@ckeditor/ckeditor5-basic-styles/src/superscript'
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote'
import HorizontalLine from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline'
import SpecialCharacters from '@ckeditor/ckeditor5-special-characters/src/specialcharacters'
import SpecialCharactersEssentials from '@ckeditor/ckeditor5-special-characters/src/specialcharactersessentials'
import Style from '@ckeditor/ckeditor5-style/src/style'
import GeneralHtmlSupport from '@ckeditor/ckeditor5-html-support/src/generalhtmlsupport'

import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice'

import WordCount from '@ckeditor/ckeditor5-word-count/src/wordcount'

import Heading from '@ckeditor/ckeditor5-heading/src/heading'

import { ImageBrowserPlugin } from './ImageBrowserPlugin'
import { NormalizeTextPlugin } from './NormalizeTextPlugin'
import { PlainTableOutput } from '@ckeditor/ckeditor5-table'
import { AdjacentListsSupport } from '@ckeditor/ckeditor5-list'

const CkEditor5 = (editorConfigurationName: string) => {

    const editorConfigurations = {
        small: {
            plugins: [
                Essentials, Paragraph,
                SourceEditing,
                Bold, Italic, Underline,
                List, ListProperties, AdjacentListsSupport,
                Indent, IndentBlock,
                Alignment,
                PageBreak,
                Link, AutoLink,
                SelectAll, RemoveFormat, NormalizeTextPlugin,
                GeneralHtmlSupport,
                Font,
                PasteFromOffice,
                PlainTableOutput,
                WordCount
            ],
            toolbar: {
                items: [
                    'sourceEditing', '|',
                    'selectAll', 'removeFormat', 'normalizeText', '|',
                    'bold', 'italic', 'underline', '|',
                    'numberedList', 'bulletedList', '|',
                    'outdent', 'indent', '|',
                    'alignment:left', 'alignment:center', 'alignment:right', 'alignment:justify', '|',
                    'fontSize', 'fontColor', '|',
                    'link', '|',
                    'pageBreak'
                ]
            },
        },
        formRow: {
            plugins: [
                Essentials, Paragraph,
                Bold, Italic, Underline,
                List, ListProperties, AdjacentListsSupport,
                GeneralHtmlSupport,
                PasteFromOffice,
                PlainTableOutput,
                WordCount
            ],
            toolbar: {
                items: [
                    'bold', 'italic', 'underline', '|',
                    'numberedList', 'bulletedList'
                ]
            }
        },
        dynamicProfile: {
            plugins: [
                Essentials, Paragraph,
                Bold, Italic, Underline,
                GeneralHtmlSupport,
                Font,
                PasteFromOffice,
                PlainTableOutput,
                WordCount
            ],
            toolbar: {
                items: [
                    'bold', 'italic', 'underline', '|',
                    'fontSize', 'fontColor'
                ]
            }
        },
        verySmall: {
            plugins: [
                SourceEditing,
                Essentials, Paragraph,
                Bold, Italic, Underline,
                SelectAll, RemoveFormat, NormalizeTextPlugin,
                GeneralHtmlSupport,
                PasteFromOffice,
                PlainTableOutput,
                WordCount
            ],
            toolbar: {
                items: [
                    'sourceEditing', '|',
                    'bold', 'italic', 'underline', '|',
                    'selectAll', 'removeFormat', 'normalizeText'
                ]
            }
        },
        resume: {
            plugins: [
                Essentials, Paragraph,
                SourceEditing,
                Bold, Italic, Underline,
                List, ListProperties, AdjacentListsSupport,
                Indent, IndentBlock,
                GeneralHtmlSupport,
                Font,
                SelectAll, RemoveFormat, NormalizeTextPlugin,
                PasteFromOffice,
                PlainTableOutput,
                WordCount
            ],
            toolbar: {
                items: [
                    'sourceEditing', '|',
                    'selectAll', 'removeFormat', 'normalizeText', '|',
                    'bold', 'italic', 'underline', '|',
                    'numberedList', 'bulletedList', '|',
                    'outdent', 'indent', '|',
                    'fontSize'
                ]
            },
        },
        full: {
            plugins: [
                Essentials, Paragraph,
                SourceEditing,
                FindAndReplace,
                SelectAll, RemoveFormat, NormalizeTextPlugin,
                Bold, Italic, Underline, Strikethrough,
                Subscript, Superscript,
                List, ListProperties, AdjacentListsSupport,
                Indent, IndentBlock,
                BlockQuote,
                Alignment,
                Link, AutoLink,
                Image, ImageToolbar, ImageUtils, ImageInline, ImageStyle, ImageResize, AutoImage, ImageBrowserPlugin, LinkImage, ImageTextAlternative,
                Table, TableToolbar, TableProperties, TableCellProperties, TableCaption, TableColumnResize,
                HorizontalLine,
                SpecialCharacters, SpecialCharactersEssentials,
                PageBreak,
                Style, GeneralHtmlSupport,
                Font,
                Heading,
                PasteFromOffice,
                PlainTableOutput,
                WordCount
            ],
            toolbar: {
                items: [
                    'sourceEditing', '|',
                    'undo', 'redo', '|',
                    'selectAll', 'removeFormat', 'normalizeText', '|',
                    'bold', 'italic', 'underline', 'strikethrough', 'subscript', 'superscript', '|',
                    'numberedList', 'bulletedList', '|',
                    'outdent', 'indent', '|',
                    'alignment:left', 'alignment:center', 'alignment:right', 'alignment:justify', '|',
                    'fontFamily', 'fontSize', 'fontColor', 'fontBackgroundColor', '|',
                    'link', '|',
                    'imageBrowser', '|',
                    'insertTable', '|',
                    'findAndReplace', '|',
                    'blockQuote', '|',
                    'horizontalLine',
                    'specialCharacters',
                    'pageBreak', '|',
                    'heading',
                    'style'
                ]
            }
        },
        writeStdDok: {
            plugins: [
                Essentials, Paragraph,
                SourceEditing,
                SelectAll, RemoveFormat, NormalizeTextPlugin,
                Bold, Italic, Underline,
                List, ListProperties, AdjacentListsSupport,
                Indent, IndentBlock,
                Alignment,
                Link, AutoLink,
                Image, ImageToolbar, ImageUtils, ImageInline, ImageStyle, ImageResize, AutoImage, ImageBrowserPlugin, LinkImage, ImageTextAlternative,
                GeneralHtmlSupport,
                Font,
                PageBreak,
                Table, TableToolbar, TableProperties, TableCellProperties, TableCaption, TableColumnResize,
                Heading,
                PasteFromOffice,
                PlainTableOutput,
                WordCount
            ],
            toolbar: {
                items: [
                    'sourceEditing', '|',
                    'undo', 'redo', '|',
                    'selectAll', 'removeFormat', 'normalizeText', '|',
                    'bold', 'italic', 'underline', '|',
                    'numberedList', 'bulletedList', '|',
                    'outdent', 'indent', '|',
                    'alignment:left', 'alignment:center', 'alignment:right', 'alignment:justify', '|',
                    'fontFamily', 'fontSize', 'fontColor', 'fontBackgroundColor', '|',
                    'link', '|',
                    'imageBrowser', '|',
                    'insertTable', '|',
                    'pageBreak', '|',
                    'heading'
                ]
            }
        },
        content: {
            plugins: [
                Essentials, Paragraph,
                SourceEditing,
                SelectAll, RemoveFormat, NormalizeTextPlugin,
                Bold, Italic, Underline,
                List, ListProperties, AdjacentListsSupport,
                Indent, IndentBlock,
                Alignment,
                Link, AutoLink,
                Image, ImageToolbar, ImageUtils, ImageInline, ImageStyle, ImageResize, AutoImage, ImageBrowserPlugin, LinkImage, ImageTextAlternative,
                GeneralHtmlSupport,
                Font,
                PasteFromOffice,
                PlainTableOutput,
                WordCount
            ],
            toolbar: {
                items: [
                    'sourceEditing', '|',
                    'undo', 'redo', '|',
                    'selectAll', 'removeFormat', 'normalizeText', '|',
                    'bold', 'italic', 'underline', '|',
                    'numberedList', 'bulletedList', '|',
                    'outdent', 'indent', '|',
                    'alignment:left', 'alignment:center', 'alignment:right', 'alignment:justify', '|',
                    'fontFamily', 'fontSize', 'fontColor', 'fontBackgroundColor', '|',
                    'link', '|',
                    'imageBrowser'
                ]
            }
        },
        smallWithFontsizeHeading: {
            plugins: [
                Essentials, Paragraph,
                SourceEditing,
                Bold, Italic, Underline,
                List, ListProperties, AdjacentListsSupport,
                Indent, IndentBlock,
                Alignment,
                PageBreak,
                Link, AutoLink,
                SelectAll, RemoveFormat,
                GeneralHtmlSupport,
                Font,
                Heading,
                PasteFromOffice,
                PlainTableOutput,
                WordCount
            ],
            toolbar: {
                items: [
                    'sourceEditing', '|',
                    'selectAll', 'removeFormat', '|',
                    'bold', 'italic', 'underline', '|',
                    'numberedList', 'bulletedList', '|',
                    'outdent', 'indent', '|',
                    'alignment:left', 'alignment:center', 'alignment:right', 'alignment:justify', '|',
                    'fontColor', 'fontSize', '|',
                    'link', '|',
                    'pageBreak', '|',
                    'heading',
                ]
            },
        },
        writeStdDokWithShowBlocks: {
            plugins: [
                Essentials, Paragraph,
                SourceEditing,
                SelectAll, RemoveFormat,
                Bold, Italic, Underline,
                List, ListProperties, AdjacentListsSupport,
                Indent, IndentBlock,
                Alignment,
                Link, AutoLink,
                Image, ImageToolbar, ImageUtils, ImageInline, ImageStyle, ImageResize, AutoImage, ImageBrowserPlugin, LinkImage, ImageTextAlternative,
                GeneralHtmlSupport,
                Font,
                PageBreak,
                Table, TableToolbar, TableProperties, TableCellProperties, TableCaption, TableColumnResize,
                Heading,
                PasteFromOffice,
                PlainTableOutput,
                WordCount,
                ShowBlocks
            ],
            toolbar: {
                items: [
                    'sourceEditing', '|',
                    'undo', 'redo', '|',
                    'showBlocks', 'selectAll', 'removeFormat', '|',
                    'bold', 'italic', 'underline', '|',
                    'numberedList', 'bulletedList', '|',
                    'outdent', 'indent', '|',
                    'alignment:left', 'alignment:center', 'alignment:right', 'alignment:justify', '|',
                    'fontFamily', 'fontSize', 'fontColor', 'fontBackgroundColor', '|',
                    'link', '|',
                    'imageBrowser', '|',
                    'insertTable', '|',
                    'pageBreak', '|',
                    'heading'
                ]
            }
        },
        none: {
            plugins: [
                Essentials, Paragraph, GeneralHtmlSupport
            ],
            toolbar: {
                items: []
            }
        }
    }

    const defaultColorsHexFormat = [
        {
            color: '#000000',
            label: 'Black'
        },
        {
            color: '#4D4D4D',
            label: 'Dim grey'
        },
        {
            color: '#999999',
            label: 'Grey'
        },
        {
            color: '#E6E6E6',
            label: 'Light grey'
        },
        {
            color: '#FFFFFF',
            label: 'White',
            hasBorder: true
        },
        {
            color: '#E64D4D',
            label: 'Red'
        },
        {
            color: '#E6994D',
            label: 'Orange'
        },
        {
            color: '#E6E64D',
            label: 'Yellow'
        },
        {
            color: '#99E64D',
            label: 'Light green'
        },
        {
            color: '#4DE64D',
            label: 'Green'
        },
        {
            color: '#4DE699',
            label: 'Aquamarine'
        },
        {
            color: '#4DE6E6',
            label: 'Turquoise'
        },
        {
            color: '#4D99E6',
            label: 'Light blue'
        },
        {
            color: '#4D4DE6',
            label: 'Blue'
        },
        {
            color: '#994DE6',
            label: 'Purple'
        }
    ]

    const editorConfigurationDefault = {
        fontSize: {
            options: [6, 8, 10, 11, 12, 13, 14, 'default', 18, 20, 22, 24, 26, 54]
        },
        fontFamily: {
            options: [
                'default',
                'Arial, Helvetica, sans-serif',
                'Courier New, Courier, monospace',
                'Georgia, serif',
                'Lucida Sans Unicode, Lucida Grande, sans-serif',
                'Tahoma, Geneva, sans-serif',
                'Times New Roman, Times, serif',
                'Trebuchet MS, Helvetica, sans-serif',
                'Verdana, Geneva, sans-serif'
            ],
            supportAllValues: true
        },
        table: {
            contentToolbar: [
                'tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties', 'toggleTableCaption'
            ]
        },
        list: {
            properties: {
                styles: true,
                startIndex: true,
                reversed: true
            }
        },
        link: {
            addTargetToExternalLinks: true,
            decorators: [
                {
                    mode: 'manual',
                    label: 'Downloadable',
                    attributes: {
                        download: 'download'
                    }
                }
            ]
        },
        image: {
            styles: [
                'alignCenter', 'alignLeft', 'alignRight'
            ],
            resizeOptions: [
                {
                    name: 'resizeImage:original', label: 'Original', value: null
                }, {
                    name: 'resizeImage:50', label: '50%', value: '50'
                }, {
                    name: 'resizeImage:75', label: '75%', value: '75'
                }
            ],
            toolbar: [
                'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText', 'imageStyle:side', '|', 'resizeImage',
                '|', 'imageTextAlternative',
                '|', 'linkImage'
            ],
            insert: {
                integrations: [
                    'insertImageViaUrl'
                ]
            },
            table: {
                contentToolbar: [
                    'tableColumn', 'tableRow', 'mergeTableCells'
                ]
            }
        },
        wordCount: {
            displayCharacters: false,
            displayWords: false
        },
        fontColor: {
            colors: defaultColorsHexFormat,
            colorPicker: {
                format: 'hex'
            }
        },
        fontBackgroundColor: {
            colors: defaultColorsHexFormat,
            colorPicker: {
                format: 'hex'
            }
        },
        // disableNativeSpellChecker: false -> does not exist in ckeditor 5, handle via native attribute 'spellcheck' on element
        htmlSupport: {
            allow: [
                {
                    name: /.*/,
                    attributes: true,
                    classes: true,
                    styles: true
                }
            ]
        },
    }

    const init = (el: Element) => {
        const editorConfigurationNameFallback = 'small'

        const attributeConfigName = el.getAttribute('data-ckeditor5-config-name')
        const attributeLanguage = el.getAttribute('data-ckeditor5-language') ?? document.documentElement.getAttribute('lang') ?? 'de'
        const attributeCss = el.getAttribute('data-ckeditor5-css')
        const attributeDisplayWords = el.getAttribute('data-ckeditor5-display-words') === 'true'
        const attributeDisplayCharacters = el.getAttribute('data-ckeditor5-display-characters') === 'true'

        const editorConfigurationsKeys = Object.keys(editorConfigurations)

        let editorConfigurationNameFinal
        if (attributeConfigName && editorConfigurationsKeys.includes(attributeConfigName)) {
            editorConfigurationNameFinal = attributeConfigName
        }
        if (!editorConfigurationNameFinal && editorConfigurationsKeys.includes(editorConfigurationName)) {
            editorConfigurationNameFinal = editorConfigurationName
        }
        if (!editorConfigurationNameFinal) {
            editorConfigurationNameFinal = editorConfigurationNameFallback
        }

        const editorConfiguration = editorConfigurations[editorConfigurationNameFinal]


        if (attributeDisplayWords || attributeDisplayCharacters) {
            editorConfigurationDefault.wordCount.displayWords = attributeDisplayWords;
            editorConfigurationDefault.wordCount.displayCharacters = attributeDisplayCharacters;
        }

        const config = {
            ...editorConfigurationDefault,
            ...editorConfiguration
        }

        return ClassicEditor
            .create(el as HTMLElement, {
                ...config,
                language: attributeLanguage,
            })
            .then(editor => {

                const editorUiViewElement = editor.ui.view.element

                if (attributeCss) {
                    const cssClasses = attributeCss.split(' ')
                    for (const cssClass of cssClasses) {
                        editorUiViewElement?.classList.add(cssClass)
                    }
                }

                if (attributeDisplayWords || attributeDisplayCharacters) {
                    if ( editor.plugins.has( 'WordCount' ) ) {
                        const wordCountPlugin = editor.plugins.get('WordCount');
                        if (wordCountPlugin instanceof WordCount) {
                            const wordCountWrapper = document.createElement("div");
                            wordCountWrapper.setAttribute('class', 'ckeditor5-word-count-wrapper');
                            editorUiViewElement?.after(wordCountWrapper);
                            wordCountWrapper.appendChild(wordCountPlugin.wordCountContainer);
                        }
                    }
                }

                return editor
            })
    }

    return {
        init
    }
}

export {
    CkEditor5
}
